/**
 * Global "out of credits" strip under the top navigation (Figma 9358:4980).
 *
 * Shown on SaaS when billing is on and the workspace balance is at or below
 * $0. Admins read the balance from ``GET billing/profile`` (following the
 * Plans & Billing tab through ``subscribeToBillingProfile`` so a purchase hides
 * it without a reload); members cannot call that admin-only endpoint, so they
 * use the ``plan`` / ``freeUsage`` / ``autoChargeEnabled`` snapshot that
 * ``/users/bootstrap`` already put in ``useAuth()``.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertBanner } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { BILLING_TAB, settingsTabPath } from '@/features/settings/components/settingsTabs.js';
import { fetchBillingProfile } from '../api/billingApi';
import { subscribeToBillingProfile } from '../hooks/useBillingProfile';
import '../billing.css';

export const OUT_OF_CREDITS_STARTER_TEXT =
    "You've used up your $20 gift credit. Upgrade to Pro to unlock unlimited actions and keep your workflows running";
export const OUT_OF_CREDITS_PRO_TEXT =
    'You are out of credits. Buy more credits to unlock unlimited actions and keep your workflows running';
export const OUT_OF_CREDITS_MEMBER_SUFFIX = ' Contact a workspace admin.';
export const OUT_OF_CREDITS_STARTER_CTA = 'Upgrade to Pro';
export const OUT_OF_CREDITS_PRO_CTA = 'Buy Credits';

// Closing the banner only hides it for the rest of this page load: module
// state survives route changes but not a reload, and nothing is persisted.
let dismissed = false;

/** Forget a dismissal; for tests and stories that share this module instance. */
export function resetOutOfCreditsBannerDismissal() {
    dismissed = false;
}

const toCents = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * Decide whether the banner shows and with which copy. Returns
 * ``{ visible, isPro, text, buttonLabel, showButton, dismiss }``.
 */
export function useOutOfCreditsBannerState() {
    const { isSaas, billingEnabled, isAuthEnabled, role, plan, freeUsage, autoChargeEnabled } = useAuth();
    const { pathname } = useLocation();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const enabled = isSaas === true && billingEnabled === true;
    // ``billing/profile`` is admin-only; members never call it.
    const adminEnabled = enabled && isAdmin;

    const [profile, setProfile] = useState(null);
    const [hidden, setHidden] = useState(dismissed);

    // Load once per route change (cheap) and follow Plans & Billing mutations
    // (buy credits, upgrade, top-up return) through the shared profile store.
    useEffect(() => {
        if (!adminEnabled) {
            setProfile(null);
            return undefined;
        }
        let active = true;
        const unsubscribe = subscribeToBillingProfile((next) => {
            if (active) setProfile(next);
        });
        fetchBillingProfile()
            .then((next) => {
                if (active) setProfile(next);
            })
            .catch(() => {
                /* keep the last known profile; a transient error must not flash the banner */
            });
        return () => {
            active = false;
            unsubscribe();
        };
    }, [adminEnabled, pathname]);

    let balanceCents = null;
    let isPro = false;
    let autoCharge = false;
    if (adminEnabled) {
        if (profile) {
            balanceCents = toCents(profile.spend_summary?.balance_cents);
            isPro = profile.is_pro === true;
            autoCharge = profile.auto_charge?.enabled === true;
        }
    } else if (enabled) {
        balanceCents = toCents(freeUsage?.balance_cents);
        isPro = plan === 'pro';
        autoCharge = autoChargeEnabled === true;
    }

    // A balance back above $0 ends the episode: the next exhaustion shows again.
    const recovered = balanceCents !== null && balanceCents > 0;
    useEffect(() => {
        if (recovered) {
            dismissed = false;
            setHidden(false);
        }
    }, [recovered]);

    const dismiss = useCallback(() => {
        dismissed = true;
        setHidden(true);
    }, []);

    const exhausted = balanceCents !== null && balanceCents <= 0;
    // Pro with auto-recharge refills on its own; nothing to ask of the user.
    const visible = exhausted && !(isPro && autoCharge) && !hidden;
    const text = (isPro ? OUT_OF_CREDITS_PRO_TEXT : OUT_OF_CREDITS_STARTER_TEXT) + (isAdmin ? '' : OUT_OF_CREDITS_MEMBER_SUFFIX);
    const buttonLabel = isAdmin ? (isPro ? OUT_OF_CREDITS_PRO_CTA : OUT_OF_CREDITS_STARTER_CTA) : null;

    return { visible, isPro, text, buttonLabel, showButton: isAdmin, dismiss };
}

export default function OutOfCreditsBanner() {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUsecaseId } = useContext(GlobalContext) || {};
    const { visible, text, buttonLabel, showButton, dismiss } = useOutOfCreditsBannerState();

    if (!visible) return null;

    const openBilling = () =>
        navigate(settingsTabPath(BILLING_TAB.id, currentUsecaseId), {
            state: { from: `${location.pathname}${location.search}${location.hash}` },
        });

    return (
        <AlertBanner
            className="out-of-credits-banner"
            feedback="warning"
            contentAlign="center"
            buttonType="default"
            text={text}
            showButton={showButton}
            buttonLabel={buttonLabel ?? undefined}
            showCloseButton
            onActionClick={openBilling}
            onCloseClick={dismiss}
        />
    );
}
